import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function parseApiResponse(raw) {
  let d = parseJsonIfString(raw);
  if (d?.body != null) return parseJsonIfString(d.body);
  return d;
}

function isApiSuccess(r) {
  if (!r || typeof r !== "object") return false;
  const s = r.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

const LrfqStatCardSkeleton = () => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const LrfqStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="procurement-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

const LRFQ_STAT_CARDS = [
  {
    key: "totalLrfqs",
    label: "Total LRFQ",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterType: null,
    filterValue: null,
  },
  {
    key: "openLrfqs",
    label: "Open-Quotes Not Received",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterType: "lrfqStatus",
    filterValue: "open",
  },
  {
    key: "quotesReceivedLrfqs",
    label: "Open-Quotes Received",
    titleColor: "#6C3483",
    valueColor: "#8E44AD",
    filterType: "lrfqStatus",
    filterValue: "Quotes Received",
  },
  {
    key: "closedLrfqs",
    label: "Closed-Quotes Not Received",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterType: "lrfqStatus",
    filterValue: "closed",
  },
  {
    key: "closedQuoteReceivedLrfqs",
    label: "Closed - Quotes Received",
    titleColor: "#5D6D7E",
    valueColor: "#85929E",
    filterType: "lrfqStatus",
    filterValue: "closed-quote received",
  },
  {
    key: "draftLrfqs",
    label: "Draft",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterType: "recordStatus",
    filterValue: "Draft",
  },
];

function extractExcelBase64(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("UEs")) return raw;
    if (t.startsWith("{")) {
      try {
        return extractExcelBase64(JSON.parse(t));
      } catch {
        return null;
      }
    }
    return null;
  }
  if (typeof raw === "object") {
    if (raw.body != null) return extractExcelBase64(raw.body);
    if (typeof raw.data === "string") return extractExcelBase64(raw.data);
    if (raw.data != null && typeof raw.data === "object") {
      const nested = extractExcelBase64(raw.data);
      if (nested) return nested;
    }
    if (raw.file != null) return extractExcelBase64(raw.file);
    if (raw.base64 != null) return extractExcelBase64(raw.base64);
  }
  return null;
}

function triggerXlsxDownloadFromBase64(base64, prefix = "LRFQs") {
  if (!base64 || typeof base64 !== "string" || !base64.startsWith("UEs")) {
    throw new Error("Invalid Excel data received");
  }
  const chars = atob(base64);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
  link.download = `${prefix}-${ts}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function extractListPayload(responseData) {
  const block = parseJsonIfString(responseData?.data ?? responseData) || {};
  const lrfqs = Array.isArray(block.lrfqs) ? block.lrfqs : [];
  return { list: lrfqs, total: block.totalCount ?? lrfqs.length };
}

const show = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "-" : v;

const capitalize = (v) => {
  if (!v) return "-";
  const s = String(v);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const formatLrfqDate = (value) => {
  if (!value) return "-";
  const s = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}/.test(s)) return s;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return s || "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const formatLrfqDateTime = (value) => {
  if (!value) return "-";
  const s = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}/.test(s)) return s;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return s || "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const lrfqCreatedByName = (row) => {
  const c = row?.createdBy;
  if (!c) return "-";
  if (typeof c === "string") return c.trim() || "-";
  return c.name || c.userName || "-";
};

const lrfqSummaryValue = (row) => {
  const s = row?.lrfqSummary || row?.summary || "";
  return String(s).trim() ? s : "-";
};

const formatLrfqStatus = (v) => {
  if (!v) return "-";
  const s = String(v).trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const quoteCountBadgeStyle = {
  padding: "1px 8px",
  fontSize: "11px",
  lineHeight: 1.4,
};

const getLrfqSuppliers = (lrfq) =>
  Array.isArray(lrfq?.suppliers)
    ? lrfq.suppliers.filter(
        (s) =>
          String(s?.supplierMailId || "").trim() ||
          String(s?.supplierName || "").trim(),
      )
    : [];

const supplierHasSubmittedQuote = (supplier) => {
  if (!supplier || typeof supplier !== "object") return false;
  if (supplier.hasQuoteSubmitted === true) return true;
  if (supplier.quoteSubmittedAt) return true;
  if (Array.isArray(supplier.materialQuotes) && supplier.materialQuotes.length > 0)
    return true;
  if (Array.isArray(supplier.terms) && supplier.terms.some((t) => t?.comments))
    return true;
  return false;
};

const renderQuoteBadge = (status, submitted, total) => (
  <span
    className="d-inline-flex align-items-center flex-wrap"
    style={{ gap: "6px" }}
  >
    <span>{status}</span>
    <span className="btn btn-sm btn-blue" style={quoteCountBadgeStyle}>
      {submitted}/{total}
    </span>
  </span>
);

function formatLrfqStatusWithQuoteCount(lrfq) {
  const baseStatus = formatLrfqStatus(lrfq?.lrfqStatus);
  const quotesSummary = lrfq?.quotesSummary;

  if (quotesSummary && typeof quotesSummary === "object") {
    const submitted =
      typeof quotesSummary.submitted === "number" ? quotesSummary.submitted : 0;
    const total =
      typeof quotesSummary.total === "number" ? quotesSummary.total : 0;
    if (total > 0) return renderQuoteBadge(baseStatus, submitted, total);
  }

  const suppliers = getLrfqSuppliers(lrfq);
  if (suppliers.length === 0) return baseStatus;

  const quotedCount =
    typeof lrfq?.quotesReceivedCount === "number"
      ? lrfq.quotesReceivedCount
      : typeof lrfq?.suppliersWithQuotes === "number"
        ? lrfq.suppliersWithQuotes
        : suppliers.filter(supplierHasSubmittedQuote).length;

  return renderQuoteBadge(baseStatus, quotedCount, suppliers.length);
}

const SORT_COLUMN_MAP = {
  lrfqNumber: "lrfqNumber",
  lrfqDate: "lrfqDate",
  lrfqDueDate: "lrfqDueDate",
  status: "status",
  lrfqStatus: "lrfqStatus",
  createdBy: "createdBy",
};

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";

const RECORD_STATUS_OPTIONS = ["Active", "Draft"];
const LRFQ_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  { value: "closed-quote received", label: "Closed - Quotes Received" },
  { value: "Quotes Received", label: "Quotes Received" },
];

const MAX_ACTIVE_FILTERS = 2;
const DEFAULT_FILTER_CHECKS = {
  recordStatus: false,
  lrfqStatus: false,
  createdBy: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  recordStatus: "",
  lrfqStatus: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};

const toApiDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  return yyyy && mm && dd ? `${dd}-${mm}-${yyyy}` : "";
};

const normalizeUsers = (users) =>
  (Array.isArray(users) ? users : [])
    .map((u) => ({
      _id: String(u._id || u.id || "").trim(),
      name: String(u.name || u.userName || u.email || "").trim(),
    }))
    .filter((u) => u._id && u.name);

function statusBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return "badge-success";
  if (normalized === "draft") return "badge-secondary";
  return "badge-info";
}

function renderRfqRecordStatus(status) {
  const normalized = String(status || "").toLowerCase();
  const label = capitalize(status);

  if (normalized === "inactive") {
    return (
      <span
        className="fwt_600 f_red"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  if (normalized === "active") {
    return (
      <span
        className="fwt_600 f_green"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={`badge ${statusBadgeClass(status)}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {label}
    </span>
  );
}

const LogisticsRfq = () => {
  const {
    canCreate: canCreateLogisticsRfq,
    canView: canViewLogisticsRfq,
    canUpdate: canUpdateLogisticsRfq,
    canDelete: canDeleteLogisticsRfq,
  } = useModuleAccess("logistics");
  const navigate = useNavigate();

  const savedFilters = JSON.parse(
    sessionStorage.getItem("logisticsRfqFilters") || "{}",
  );

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState(
    savedFilters.filterChecks || DEFAULT_FILTER_CHECKS,
  );
  const [filterInputs, setFilterInputs] = useState(
    savedFilters.filterInputs || DEFAULT_FILTER_INPUTS,
  );
  const [users, setUsers] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const filterPanelRef = useRef(null);

  const anyFilterActive = Boolean(
    filterInputs.recordStatus ||
    filterInputs.lrfqStatus ||
    filterInputs.createdBy ||
    filterInputs.fromDate ||
    filterInputs.toDate,
  );

  const [deleteId, setDeleteId] = useState(null);
  const [deleteLabel, setDeleteLabel] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const [isCountLoading, setIsCountLoading] = useState(true);
  const [lrfqCount, setLrfqCount] = useState({
    totalLrfqs: 0,
    openLrfqs: 0,
    closedLrfqs: 0,
    closedQuoteReceivedLrfqs: 0,
    quotesReceivedLrfqs: 0,
    poCreatedLrfqs: 0,
    poPartiallyCreatedLrfqs: 0,
    draftLrfqs: 0,
  });
  const countRequestIdRef = useRef(0);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("all");
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const loadRequestIdRef = useRef(0);

  const buildFilterFields = useCallback(
    (fields = {}, { includeStatusFilters = true } = {}) => {
      if (includeStatusFilters && filterInputs.recordStatus)
        fields.status = filterInputs.recordStatus;
      if (includeStatusFilters && filterInputs.lrfqStatus)
        fields.lrfqStatus = filterInputs.lrfqStatus;
      if (filterInputs.createdBy) fields.createdBy = filterInputs.createdBy;
      if (filterInputs.fromDate)
        fields.fromDate = toApiDDMMYYYY(filterInputs.fromDate);
      if (filterInputs.toDate)
        fields.toDate = toApiDDMMYYYY(filterInputs.toDate);
      return fields;
    },
    [filterInputs],
  );

  const fetchList = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-lrfqs",
        page: currentPage,
        limit: itemsPerPage,
      };
      const trimmed = activeSearchQuery.trim();
      if (trimmed) payload.search = trimmed;
      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      buildFilterFields(payload);

      const response = await axios.post(API_URL, payload);
      if (requestId !== loadRequestIdRef.current) return;

      const responseData = parseApiResponse(response.data);
      const { list, total } = extractListPayload(responseData);

      if (isApiSuccess(responseData) || list.length > 0) {
        setRows(list);
        setTotalCount(total);
      } else {
        toast.error(responseData?.message || "Failed to fetch Logistics RFQs", {
          autoClose: 2000,
        });
        setRows([]);
        setTotalCount(0);
      }
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error("Fetch Logistics RFQs error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while fetching Logistics RFQs",
        { autoClose: 2000 },
      );
      setRows([]);
      setTotalCount(0);
    } finally {
      if (requestId === loadRequestIdRef.current) setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    sortBy,
    sortOrder,
    buildFilterFields,
  ]);
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const fetchCount = useCallback(async () => {
    const requestId = ++countRequestIdRef.current;
    setIsCountLoading(true);
    try {
      const payload = { action: "lrfq-count" };
      const trimmed = activeSearchQuery.trim();
      if (trimmed) payload.search = trimmed;
      buildFilterFields(payload, { includeStatusFilters: false });

      const response = await axios.post(API_URL, payload);
      if (requestId !== countRequestIdRef.current) return;

      const data = parseApiResponse(response.data);
      if (isApiSuccess(data)) {
        const s = data.status || {};
        setLrfqCount({
          totalLrfqs: s.totalLrfqs || 0,
          openLrfqs: s.openLrfqs || 0,
          closedLrfqs: s.closedLrfqs || 0,
          closedQuoteReceivedLrfqs: s.closedQuoteReceivedLrfqs || 0,
          quotesReceivedLrfqs: s.quotesReceivedLrfqs || 0,
          poCreatedLrfqs: s.poCreatedLrfqs || 0,
          poPartiallyCreatedLrfqs: s.poPartiallyCreatedLrfqs || 0,
          draftLrfqs: s.draftLrfqs || 0,
        });
      }
    } catch (error) {
      if (requestId === countRequestIdRef.current)
        console.error("LRFQ count error:", error);
    } finally {
      if (requestId === countRequestIdRef.current) setIsCountLoading(false);
    }
  }, [activeSearchQuery, buildFilterFields]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    const handler = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsUsersLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-lrfq-users",
        });
        const data = parseApiResponse(response.data);
        if (!cancelled && isApiSuccess(data) && Array.isArray(data.data)) {
          setUsers(normalizeUsers(data.data));
        }
      } catch (error) {
        if (!cancelled) console.error("Fetch users error:", error);
      } finally {
        if (!cancelled) setIsUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "logisticsRfqFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
        filterChecks,
        filterInputs,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    filterChecks,
    filterInputs,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = rows.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const max = 7;
    const tp = totalPages;
    if (tp <= max) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis", tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1, "ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1, "ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis", tp);
    }
    return pages;
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    if (v.trim() === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

  const handleFilterCheckToggle = (key) => {
    const isChecked = filterChecks[key];

    if (!isChecked) {
      const activeCount = Object.values(filterChecks).filter(Boolean).length;
      if (activeCount >= MAX_ACTIVE_FILTERS) {
        toast.error(`You can select up to ${MAX_ACTIVE_FILTERS} filters only`, {
          autoClose: 2000,
        });
        return;
      }
    }

    setFilterChecks((prev) => ({ ...prev, [key]: !isChecked }));

    if (isChecked) {
      setFilterInputs((prev) =>
        key === "dateRange"
          ? { ...prev, fromDate: "", toDate: "" }
          : { ...prev, [key]: "" },
      );
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;
      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }
    }
    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSort = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy(DEFAULT_SORT_BY);
        setSortOrder(DEFAULT_SORT_ORDER);
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    if (currentPage !== 1) setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    const base = { fontSize: "0.8em", flexShrink: 0 };
    if (sortBy !== apiField)
      return <i className="fa-solid fa-sort text-muted ml-1" style={base} />;
    return sortOrder === "asc" ? (
      <i className="fa-solid fa-sort-up text-primary ml-1" style={base} />
    ) : (
      <i className="fa-solid fa-sort-down text-primary ml-1" style={base} />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{ cursor: "pointer", whiteSpace: "nowrap", ...extraStyle }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const handleCreateClick = () => {
    if (window.$) window.$("#modalcreatelogisticsrfq").modal("show");
  };

  const handleConfirmCreate = () => {
    if (window.$) window.$("#modalcreatelogisticsrfq").modal("hide");
    navigate("/logisticsRfqCreate");
  };

  const handleDeleteClick = (row) => {
    const rid = row?._id || row?.id;
    if (!rid) {
      toast.error("Logistics RFQ ID is missing", { autoClose: 2000 });
      return;
    }
    setDeleteId(rid);
    setDeleteLabel(row.lrfqNumber || "-");
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const requestUserId = localStorage.getItem("id");
    if (!requestUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-lrfq",
        id: deleteId,
        requestUserId,
      });
      const responseData = parseApiResponse(response.data);

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Logistics RFQ deleted successfully",
          { autoClose: 2000 },
        );
        if (window.$) window.$("#modaldeletelogisticsrfq").modal("hide");
        setDeleteId(null);
        setDeleteLabel("");
        if (rows.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
          fetchCount();
        } else {
          await fetchCount();
          await fetchList();
        }
      } else {
        toast.error(responseData?.message || "Failed to delete Logistics RFQ", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete Logistics RFQ error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while deleting Logistics RFQ",
        { autoClose: 2000 },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const isStatCardActive = (card) => {
    if (!card.filterValue)
      return !filterInputs.lrfqStatus && !filterInputs.recordStatus;
    return filterInputs[card.filterType] === card.filterValue;
  };

  const handleStatCardClick = (card) => {
    const key = card.filterType; // "lrfqStatus" | "recordStatus" | null

    if (!key) {
      setFilterChecks((p) => ({
        ...p,
        lrfqStatus: false,
        recordStatus: false,
      }));
      setFilterInputs((p) => ({ ...p, lrfqStatus: "", recordStatus: "" }));
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }

    if (!filterChecks[key]) {
      const otherActive = Object.keys(filterChecks).filter(
        (k) => k !== key && filterChecks[k],
      );
      if (otherActive.length >= MAX_ACTIVE_FILTERS) {
        const remove = otherActive[0];
        const cleared =
          remove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [remove]: "" };
        setFilterChecks((p) => ({ ...p, [remove]: false, [key]: true }));
        setFilterInputs((p) => ({ ...p, ...cleared, [key]: card.filterValue }));
        toast.info("Replaced an existing filter to apply this status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((p) => ({ ...p, [key]: true }));
        setFilterInputs((p) => ({ ...p, [key]: card.filterValue }));
      }
    } else {
      setFilterInputs((p) => ({ ...p, [key]: card.filterValue }));
    }

    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleExport = async () => {
    if (exportMode === "date") {
      if (!exportFromDate || !exportToDate) {
        toast.error("Please select both from date and to date", {
          autoClose: 2000,
        });
        return;
      }
      if (new Date(exportFromDate) > new Date(exportToDate)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }
    }

    setIsExporting(true);
    try {
      const payload = { action: "lrfq-export" };
      const trimmed = activeSearchQuery.trim();
      if (trimmed) payload.search = trimmed;
      buildFilterFields(payload);

      // export-modal dates win over the list's date filter
      if (exportMode === "date") {
        payload.fromDate = toApiDDMMYYYY(exportFromDate);
        payload.toDate = toApiDDMMYYYY(exportToDate);
      }

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });
      const base64 = extractExcelBase64(response.data);
      if (!base64) throw new Error("Invalid Excel data received from server");

      triggerXlsxDownloadFromBase64(base64, "LRFQs");
      toast.success("LRFQs exported successfully.", { autoClose: 2000 });
      setShowExportModal(false);
    } catch (error) {
      console.error("Export LRFQs error:", error);
      toast.error("Failed to export LRFQs.", { autoClose: 2000 });
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilters = Boolean(activeSearchQuery || anyFilterActive);

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Logistics RFQ</h1>
            </div>
            <div className="col-sm-6">
              <div className="d-flex justify-content-end align-items-center">
                {canCreateLogisticsRfq && (
                  <button
                    type="button"
                    className="btn btn-blue mr-2"
                    onClick={handleCreateClick}
                  >
                    Create Logistics RFQ
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => setShowExportModal(true)}
                  disabled={isExporting}
                >
                  <i
                    className={`fa-solid ${isExporting ? "fa-spinner fa-spin" : "fa-file-export"} mr-1`}
                  />
                  {isExporting ? "Exporting..." : "Export"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row rfq-stat-card-row">
                {isCountLoading
                  ? LRFQ_STAT_CARDS.map((card) => (
                      <LrfqStatCardSkeleton key={card.key} />
                    ))
                  : LRFQ_STAT_CARDS.map((card) => (
                      <LrfqStatCard
                        key={card.key}
                        label={card.label}
                        value={lrfqCount[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isStatCardActive(card)}
                        onClick={() => handleStatCardClick(card)}
                      />
                    ))}
              </div>
              <div className="card-body">
                <div className="row align-items-end mb-4 flex-xl-nowrap">
                  <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Search
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search Logistics RFQs..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa-solid fa-search" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                    <div
                      className="form-group mb-0 position-relative"
                      ref={filterPanelRef}
                    >
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Filter
                      </label>
                      <button
                        type="button"
                        className="form-control text-left d-flex justify-content-between align-items-center"
                        onClick={() => setIsFilterPanelOpen((open) => !open)}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {anyFilterActive ? "Filters" : "Select Filters"}
                        </span>
                        <i
                          className={`fa-solid fa-chevron-${isFilterPanelOpen ? "up" : "down"}`}
                          style={{
                            fontSize: "11px",
                            flexShrink: 0,
                            marginLeft: "6px",
                          }}
                        />
                      </button>

                      {isFilterPanelOpen && (
                        <div
                          style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            marginTop: "4px",
                            background: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "10px 12px",
                            width: "200px",
                            maxWidth: "calc(100vw - 32px)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                          }}
                        >
                          {[
                            {
                              key: "recordStatus",
                              id: "lfCheckRecordStatus",
                              label: "Status",
                            },
                            {
                              key: "lrfqStatus",
                              id: "lfCheckLrfqStatus",
                              label: "LRFQ Status",
                            },
                            {
                              key: "createdBy",
                              id: "lfCheckCreatedBy",
                              label: "Created User",
                            },
                            {
                              key: "dateRange",
                              id: "lfCheckDateRange",
                              label: "Date",
                            },
                          ].map((filter) => {
                            const activeCount =
                              Object.values(filterChecks).filter(
                                Boolean,
                              ).length;
                            return (
                              <div
                                className="custom-control custom-checkbox mb-2"
                                key={filter.key}
                              >
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={filter.id}
                                  checked={filterChecks[filter.key]}
                                  disabled={
                                    !filterChecks[filter.key] &&
                                    activeCount >= MAX_ACTIVE_FILTERS
                                  }
                                  onChange={() =>
                                    handleFilterCheckToggle(filter.key)
                                  }
                                />
                                <label
                                  className="custom-control-label"
                                  htmlFor={filter.id}
                                >
                                  {filter.label}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {filterChecks.recordStatus && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Status
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.recordStatus}
                          onChange={(e) =>
                            handleFilterInputChange(
                              "recordStatus",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select Status</option>
                          {RECORD_STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.lrfqStatus && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          LRFQ Status
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.lrfqStatus}
                          onChange={(e) =>
                            handleFilterInputChange(
                              "lrfqStatus",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select LRFQ Status</option>
                          {LRFQ_STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.createdBy && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Created User
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.createdBy}
                          onChange={(e) =>
                            handleFilterInputChange("createdBy", e.target.value)
                          }
                        >
                          <option value="">
                            {isUsersLoading && users.length === 0
                              ? "Loading users..."
                              : "Select User"}
                          </option>
                          {users.map((u) => (
                            <option key={u._id} value={u._id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.dateRange && (
                    <>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.fromDate || ""}
                            max={filterInputs.toDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "fromDate",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.toDate || ""}
                            min={filterInputs.fromDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange("toDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {anyFilterActive && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm w-100"
                        onClick={handleClearFilter}
                        style={{ minWidth: "100px", whiteSpace: "nowrap" }}
                      >
                        <i className="fa-solid fa-times mr-1" />
                        Clear Filter
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isLoading && rows.length === 0 ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : rows.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no Logistics RFQs yet.
                  <br />
                  {canCreateLogisticsRfq && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleCreateClick}
                      >
                        Create Logistics RFQ
                      </button>
                    </div>
                  )}
                </div>
              ) : rows.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No Logistics RFQs found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="supply-table-container"
                  style={{ position: "relative" }}
                >
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.55)",
                        pointerEvents: "none",
                      }}
                    >
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  )}

                  <div
                    className="table-responsive"
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s ease",
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <table
                      id="logisticsRfqs"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("lrfqNumber", "LRFQ No", {
                            minWidth: "120px",
                          })}
                          {renderSortableHeader("lrfqDate", "LRFQ Date", {
                            minWidth: "110px",
                          })}
                          {renderSortableHeader(
                            "lrfqDueDate",
                            "LRFQ Due Date",
                            { minWidth: "140px" },
                          )}
                          <th style={{ minWidth: "200px" }}>Summary</th>
                          {renderSortableHeader("status", "Status", {
                            minWidth: "90px",
                          })}
                          {renderSortableHeader("lrfqStatus", "LRFQ Status", { minWidth: "110px" })}
                          {renderSortableHeader("createdBy", "Created User", {
                            minWidth: "140px",
                          })}
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => {
                          const rid = row._id || row.id;
                          return (
                            <tr key={rid || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {show(row.lrfqNumber)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatLrfqDate(row.lrfqDate)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatLrfqDateTime(row.lrfqDueDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {lrfqSummaryValue(row)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderRfqRecordStatus(row.status)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                               {formatLrfqStatusWithQuoteCount(row)}
                             </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {lrfqCreatedByName(row)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canViewLogisticsRfq && (
                                    <Link
                                      to={`/logisticsRfqView/${rid}`}
                                      className="crsr_pntr mr-2"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </Link>
                                  )}
                                  {canUpdateLogisticsRfq && (
                                    <Link
                                      to={`/logisticsRfqCreate/${rid}`}
                                      className="crsr_pntr mr-2"
                                      title="Edit"
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </Link>
                                  )}
                                  {canDeleteLogisticsRfq && (
                                    <a
                                      href="#modaldeletelogisticsrfq"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldeletelogisticsrfq"
                                      title="Delete"
                                      onClick={() => handleDeleteClick(row)}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="supply-pagination-container"
                    style={{
                      opacity: isLoading ? 0.65 : 1,
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <div className="pagination-wrapper">
                      <div className="pagination-info">
                        <label
                          className="mb-0 mr-2"
                          style={{ fontSize: "13px" }}
                        >
                          Show:
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                        <span className="ml-2" style={{ fontSize: "13px" }}>
                          entries
                        </span>
                        <span
                          className="ml-3 text-muted"
                          style={{ fontSize: "13px" }}
                        >
                          {startItem}–{endItem} of {totalCount}
                        </span>
                      </div>

                      <nav className="pagination-nav">
                        <ul className="pagination mb-0">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              title="First Page"
                            >
                              <i className="fa-solid fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              title="Previous Page"
                            >
                              <i className="fa-solid fa-angle-left"></i>
                            </button>
                          </li>

                          {getPageNumbers().map((page, index) =>
                            page === "ellipsis" ? (
                              <li
                                key={`ellipsis-${index}`}
                                className="page-item disabled"
                              >
                                <span className="page-link">...</span>
                              </li>
                            ) : (
                              <li
                                key={page}
                                className={`page-item ${currentPage === page ? "active" : ""}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ),
                          )}

                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              title="Next Page"
                            >
                              <i className="fa-solid fa-angle-right"></i>
                            </button>
                          </li>
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              title="Last Page"
                            >
                              <i className="fa-solid fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalcreatelogisticsrfq"
        tabIndex={-1}
        aria-labelledby="modalcreatelogisticsrfqLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalcreatelogisticsrfqLabel">
                Create Logistics RFQ
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to create Logistics RFQ?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                No
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleConfirmCreate}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldeletelogisticsrfq"
        tabIndex={-1}
        aria-labelledby="modaldeletelogisticsrfqLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeletelogisticsrfqLabel">
                Delete Logistics RFQ
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">{deleteLabel || "-"}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade ${showExportModal ? "show" : ""}`}
        style={{ display: showExportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalExportLrfqLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false);
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportLrfqLabel">
                Export Logistics RFQs
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => !isExporting && setShowExportModal(false)}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {hasActiveFilters && (
                <p className="text-muted small mb-3">
                  Current filters will be included:
                  {activeSearchQuery && (
                    <span className="d-block">Search: {activeSearchQuery}</span>
                  )}
                  {filterInputs.lrfqStatus && (
                    <span className="d-block">
                      LRFQ Status: {filterInputs.lrfqStatus}
                    </span>
                  )}
                  {filterInputs.recordStatus && (
                    <span className="d-block">
                      Status: {filterInputs.recordStatus}
                    </span>
                  )}
                  {filterInputs.createdBy && (
                    <span className="d-block">Created User selected</span>
                  )}
                  {(filterInputs.fromDate || filterInputs.toDate) && (
                    <span className="d-block">
                      Date:{" "}
                      {filterInputs.fromDate
                        ? toApiDDMMYYYY(filterInputs.fromDate)
                        : "-"}{" "}
                      to{" "}
                      {filterInputs.toDate
                        ? toApiDDMMYYYY(filterInputs.toDate)
                        : "-"}
                    </span>
                  )}
                </p>
              )}
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllLrfqs"
                    name="exportTypeLrfqs"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === "all"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportAllLrfqs"
                  >
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDateLrfqs"
                    name="exportTypeLrfqs"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === "date"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportDateLrfqs"
                  >
                    Filter by date
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    max={exportToDate || undefined}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    min={exportFromDate || undefined}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default LogisticsRfq;
